import React, {useEffect, useState} from "react";
import {Button, Descriptions, Form, Input, Modal, Space, Spin, Upload} from "antd";
import {message} from "../../utils/antdFeedback";
import {UploadOutlined, ArrowLeftOutlined} from "@ant-design/icons";
import {RefreshCcw} from "lucide-react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {AppDispatch, RootState} from "../../redux/store";
import {approvePaymentLinkRefundRequest, getPaymentLinkRefundRequestById, rejectPaymentLinkRefundRequest} from "../../redux/slice/paymentLinkSlice";
import {adminRequest} from "../../api/adminApi";
import {HeroHeader, Card, formatNaira, COLORS} from "../../designSystem";

const money = (value: any, currency = "NGN") =>
  !currency || currency === "NGN" ? formatNaira(value) : `${currency} ${Number(value || 0).toLocaleString()}`;

function ApprovePaymentLinkRefund() {
  const {id} = useParams();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const {token} = useSelector((state: RootState) => state.account);
  const request = useSelector((state: RootState) => state.paymentLinks.selectedRefundRequest);
  const [proofUrl, setProofUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isCurrentRequest = request && String(request.id) === String(id);

  useEffect(() => {
    if (token && id) {
      dispatch(getPaymentLinkRefundRequestById({token, requestId: id}));
    }
  }, [token, id, dispatch]);

  const uploadProof = async (file: any) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const result: any = await adminRequest("/file/document", {method: "POST", token, body: formData});
      setProofUrl(result?.returnedObject?.fileUrl || "");
      message.success("Proof uploaded");
    } catch {
      message.error("Upload failed");
    }
    return false;
  };

  const approve = async (values: any) => {
    if (!token || !id) return;
    setSubmitting(true);
    try {
      await dispatch(approvePaymentLinkRefundRequest({
        token,
        requestId: id,
        externalReference: values.externalReference,
        paymentProofUrl: proofUrl,
        narration: values.narration,
      })).unwrap();
      message.success("Refund approved");
      navigate("/payment-link-refunds");
    } catch (error: any) {
      message.error(error || "Approval failed");
    } finally {
      setSubmitting(false);
    }
  };

  const reject = () => {
    Modal.confirm({
      title: "Reject refund request",
      content: (
        <Form id="reject-refund-form" layout="vertical" onFinish={async (values) => {
          if (!token || !id) return;
          await dispatch(rejectPaymentLinkRefundRequest({token, requestId: id, reason: values.reason})).unwrap();
          message.success("Refund rejected");
          navigate("/payment-link-refunds");
        }}>
          <Form.Item name="reason" label="Reason" rules={[{required: true, message: "Enter a rejection reason"}]}>
            <Input.TextArea rows={3}/>
          </Form.Item>
        </Form>
      ),
      okText: "Reject",
      okButtonProps: {danger: true, htmlType: "submit", form: "reject-refund-form"},
    });
  };

  if (!isCurrentRequest) {
    return <AdminLayout><div className="m-10"><Spin tip="Loading request..."/></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
        <div className="mx-auto max-w-3xl">
          <Button className="mb-4" icon={<ArrowLeftOutlined/>} onClick={() => navigate(-1)}>Back</Button>
          <HeroHeader
            icon={RefreshCcw}
            title="Approve Payment Link Refund"
            subtitle="Confirm refund destination and payout proof before completion."
          />
          <Card className="mt-8 p-5">
            <Descriptions bordered column={1} className="mb-8">
              <Descriptions.Item label="Creator">{request.creatorEmail}</Descriptions.Item>
              <Descriptions.Item label="Amount"><span className="font-mono">{money(request.amount, request.currency)}</span></Descriptions.Item>
              <Descriptions.Item label="Bank">{request.bankName}</Descriptions.Item>
              <Descriptions.Item label="Account">{request.accountName} {request.accountNumber}</Descriptions.Item>
              <Descriptions.Item label="Reason">{request.reason}</Descriptions.Item>
              <Descriptions.Item label="Status">{String(request.status)}</Descriptions.Item>
            </Descriptions>
            <Form layout="vertical" onFinish={approve}>
              <Form.Item label="External Reference" name="externalReference">
                <Input/>
              </Form.Item>
              <Form.Item label="Narration" name="narration">
                <Input.TextArea rows={3}/>
              </Form.Item>
              <Form.Item label="Payment Proof">
                <Upload beforeUpload={uploadProof} showUploadList={false}>
                  <Button icon={<UploadOutlined/>}>Upload Proof</Button>
                </Upload>
                {proofUrl && <a className="mt-2 block" href={proofUrl} target="_blank" rel="noreferrer">View uploaded proof</a>}
              </Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={submitting}>Approve</Button>
                <Button danger onClick={reject}>Reject</Button>
              </Space>
            </Form>
          </Card>
        </div>
      </main>
    </AdminLayout>
  );
}

export default ApprovePaymentLinkRefund;
